import { useEffect, useState } from "react";
import { getItemTranslation } from "../domains/app/utils/getItemTranslation";
import { apiClient, type Api } from "../services/api";
import BottomPanel from "./app/panels/BottomPanel";
import CustomIcon from "./CustomIcon";
import ProfilePicture from "./ProfilePicture";
import { useTranslation } from "react-i18next";

type UserById = Extract<
  Api["get"]["/webapp/public/users/:id/share-preview"]["output"],
  { status: 200 }
>["body"];

type Props = {
  type?: "profile" | "playlist";
};

export default function SharedBySheet(props: Props) {
  const { type } = props;
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [sharingUser, setSharingUser] = useState<UserById | null>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(globalThis.location.search);
    const sharingUserId = searchParams.get("shared");

    if (sharingUserId && !sharingUser) {
      void (async () => {
        try {
          const { body } = await apiClient.get(
            "/webapp/public/users/:id/share-preview",
            {
              param: { id: sharingUserId },
            }
          );
          setSharingUser(body);
          setIsVisible(true);
          // Only `shared` goes: it names the sharer of THIS visit, and a reader
          // who copies the address bar must not pass that credit on. The
          // campaign (`utm_*`) and the playlist `claim` stay in the URL.
          const url = new URL(globalThis.location.href);
          url.searchParams.delete("shared");
          history.replaceState(null, "", url.pathname + url.search);
        } catch (error) {
          console.warn("Couldn't resolve shared user preview.", error);
        }
      })();
    }
  }, [sharingUser]);

  return (
    <BottomPanel
      isOpen={isVisible}
      onClose={() => {
        setIsVisible(false);
      }}
      displayCloseButton
    >
      <div className="pt-[20px] px-[24px] pb-[64px] bg-white">
        <div className="flex flex-col items-center">
          <span className="text-[24px] font-bold">
            {sharingUser?.firstname
              ? `${sharingUser.firstname} ${sharingUser.lastname?.at(0) ?? ""}.`
              : t("A peer")}
          </span>
          <span className="text-[16px]">
            {type === "profile"
              ? t("has shared this profile with you")
              : type === "playlist"
                ? t("has shared this playlist with you")
                : t("has shared this content with you")}
          </span>
          <div className="flex flex-col items-center justify-center gap-[12px] mt-[32px]">
            {sharingUser?.firstname ? (
              <ProfilePicture user={sharingUser} height={48} badge={null} />
            ) : (
              <CustomIcon scale={1.5} iconName="user-circle" color="#000000" />
            )}
            {sharingUser !== null && (
              <div className="text-center text-[14px]">
                {sharingUser.professionModel === null ? null : (
                  <b>{getItemTranslation(sharingUser.professionModel)}</b>
                )}
                <br />
                {sharingUser.mainSpecialtyModel === null
                  ? null
                  : getItemTranslation(sharingUser.mainSpecialtyModel)}
              </div>
            )}
          </div>
        </div>
      </div>
    </BottomPanel>
  );
}
